import { useState, useEffect, useCallback } from 'react';
import type { AssetStatus } from '@/types/service';

interface UseAssetValidatorOptions {
  /** Maximum file size in bytes (default: 500KB) */
  maxSize?: number;
  /** Timeout in milliseconds (default: 5000) */
  timeout?: number;
  /** Debounce delay in milliseconds (default: 300) */
  debounce?: number;
}

interface UseAssetValidatorReturn {
  /** Current validation status */
  status: AssetStatus;
  /** Validated/loaded URL (for display) */
  loadedUrl: string | null;
  /** Manually trigger validation */
  validate: (url: string) => void;
  /** Reset to empty state */
  reset: () => void;
}

const DEFAULT_MAX_SIZE = 500 * 1024; // 500 KB
const DEFAULT_TIMEOUT = 5000; // 5 seconds
const DEFAULT_DEBOUNCE = 300; // 300ms

/**
 * Hook to validate and load external assets (images).
 *
 * Validates:
 * - URL format (must be HTTPS)
 * - File size (default max 500KB via HEAD request)
 * - Reachability (CORS, timeout)
 * - For images: actual loading via Image element
 */
export function useAssetValidator(
  url: string | undefined,
  type: 'image' | 'stylesheet',
  options: UseAssetValidatorOptions = {}
): UseAssetValidatorReturn {
  const {
    maxSize = DEFAULT_MAX_SIZE,
    timeout = DEFAULT_TIMEOUT,
    debounce = DEFAULT_DEBOUNCE,
  } = options;

  const [status, setStatus] = useState<AssetStatus>({ status: 'empty' });
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);

  const validateUrl = useCallback(async (urlToValidate: string) => {
    // Empty URL
    if (!urlToValidate || urlToValidate.trim() === '') {
      setStatus({ status: 'empty' });
      setLoadedUrl(null);
      return;
    }

    // URL format validation
    if (!urlToValidate.startsWith('https://')) {
      setStatus({ status: 'error', message: 'URL muss mit https:// beginnen' });
      setLoadedUrl(null);
      return;
    }

    // Start loading
    setStatus({ status: 'loading' });

    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      // First, do a HEAD request to check size (if server supports it)
      let size = 0;
      try {
        const headResponse = await fetch(urlToValidate, {
          method: 'HEAD',
          mode: 'cors',
          signal: controller.signal,
        });

        if (!headResponse.ok) {
          clearTimeout(timeoutId);
          setStatus({ status: 'error', message: `HTTP ${headResponse.status}` });
          setLoadedUrl(null);
          return;
        }

        const contentLength = headResponse.headers.get('content-length');
        if (contentLength) {
          size = parseInt(contentLength, 10);
          if (size > maxSize) {
            clearTimeout(timeoutId);
            const sizeKB = Math.round(size / 1024);
            const maxKB = Math.round(maxSize / 1024);
            setStatus({
              status: 'error',
              message: `Datei zu groß (${sizeKB} KB > ${maxKB} KB)`,
            });
            setLoadedUrl(null);
            return;
          }
        }
      } catch {
        // HEAD request failed - some servers don't support HEAD or have CORS issues with HEAD
        // Continue with image loading as fallback
      }

      clearTimeout(timeoutId);

      // For images: actually load the image
      if (type === 'image') {
        await loadImage(urlToValidate, timeout);
      }

      setStatus({ status: 'success', size, url: urlToValidate });
      setLoadedUrl(urlToValidate);
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          setStatus({ status: 'error', message: 'Timeout beim Laden' });
        } else if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
          setStatus({ status: 'error', message: 'CORS-Fehler oder nicht erreichbar' });
        } else {
          setStatus({ status: 'error', message: error.message });
        }
      } else {
        setStatus({ status: 'error', message: 'Unbekannter Fehler' });
      }
      setLoadedUrl(null);
    }
  }, [maxSize, timeout, type]);

  // Debounced validation on URL change
  useEffect(() => {
    if (!url) {
      setStatus({ status: 'empty' });
      setLoadedUrl(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      validateUrl(url);
    }, debounce);

    return () => clearTimeout(timeoutId);
  }, [url, debounce, validateUrl]);

  const reset = useCallback(() => {
    setStatus({ status: 'empty' });
    setLoadedUrl(null);
  }, []);

  return {
    status,
    loadedUrl,
    validate: validateUrl,
    reset,
  };
}

/**
 * Load an image and resolve when loaded.
 */
function loadImage(url: string, timeout: number): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timeoutId = setTimeout(() => {
      img.src = '';
      reject(new Error('Timeout beim Laden'));
    }, timeout);

    img.onload = () => {
      clearTimeout(timeoutId);
      resolve(img);
    };

    img.onerror = () => {
      clearTimeout(timeoutId);
      reject(new Error('Bild konnte nicht geladen werden'));
    };

    // Enable CORS for the image
    img.crossOrigin = 'anonymous';
    img.src = url;
  });
}
